import { z } from 'zod';
import { pageQuerySchema } from './http.js';
import { localeCodeSchema } from './i18n.js';
/**
 * docs/004 requires closed sets to be enums but never names the members for
 * `Tenant.status`. Chosen here and recorded in docs/adr/0011-lifecycle-status-enums.md.
 * The Prisma enum in packages/db must stay identical to this list.
 */
export const TENANT_STATUSES = ['PENDING', 'ACTIVE', 'SUSPENDED'];
export const tenantStatusSchema = z.enum(TENANT_STATUSES);
/**
 * The wire shape of a tenant. An allow-list, deliberately narrower than the row:
 * `branding` is not exposed here — it is served by the theming endpoint when that
 * lands, so adding a column can never widen this response (docs/008 invariant S7).
 */
export const tenantDTOSchema = z.object({
    id: z.string().min(1),
    slug: z.string().min(1),
    name: z.string().min(1),
    status: tenantStatusSchema,
    defaultLocale: localeCodeSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** The `q` bounds on `GET /api/web/tenants`. Exported so the console can skip a draft the API would reject. */
export const TENANT_SEARCH_MIN_LENGTH = 2;
export const TENANT_SEARCH_MAX_LENGTH = 100;
/**
 * `GET /api/web/tenants` — the operator's cross-tenant list.
 *
 * `q` is ONE bounded term matched case-insensitively against the tenant's name OR slug, with the
 * same `min(2)` floor every other search in this package uses (`agreementListQuerySchema`,
 * `visitFilterQuerySchema.q`). Paging is the shared `pageQuerySchema`.
 */
export const tenantListQuerySchema = pageQuerySchema.merge(z.object({
    q: z.string().trim().min(TENANT_SEARCH_MIN_LENGTH).max(TENANT_SEARCH_MAX_LENGTH).optional(),
}));
/** Reserved slugs that would collide with a routing prefix or an infra hostname. */
export const RESERVED_TENANT_SLUGS = ['api', 'admin', 'www', 'app', 'visitor', 'static'];
export const tenantSlugSchema = z
    .string()
    .min(2)
    .max(63)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .refine((slug) => !RESERVED_TENANT_SLUGS.includes(slug), {
    message: 'RESERVED_SLUG',
});
/** Longest base `deriveTenantSlug` returns, leaving room under the 63-char cap for a `-<n>` / `-<hex>` suffix. */
const DERIVED_SLUG_MAX = 48;
/** What a name with no Latin letters or digits (CJK, emoji, punctuation only) falls back to. */
const DERIVED_SLUG_FALLBACK = 'org';
/**
 * Derive a slug BASE from an organization name, for signups that do not choose one.
 *
 * Pure and browser-safe. Lowercases, strips diacritics (NFKD, combining marks removed), collapses
 * every run of non `[a-z0-9]` into one hyphen, trims hyphens and caps at 48 characters. A result
 * shorter than 2 characters (including empty, e.g. a non-Latin name) becomes `org`. A reserved
 * result (e.g. "Admin") gets an `-org` suffix, so the return value ALWAYS satisfies
 * `tenantSlugSchema`. It is a base only: uniqueness is the caller's job (append `-2`, `-3`, ...).
 */
export function deriveTenantSlug(name) {
    const base = name
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, DERIVED_SLUG_MAX)
        .replace(/-+$/g, '');
    const slug = base.length < 2 ? DERIVED_SLUG_FALLBACK : base;
    return RESERVED_TENANT_SLUGS.includes(slug) ? `${slug}-org` : slug;
}
//# sourceMappingURL=tenant.js.map